import * as React from 'react'

import { ChevronsUpDown } from 'lucide-react'

import { FieldTrigger } from '~/client/src/shared/components/FieldTrigger'
import { mapboxFeatureToAddress } from '~/client/src/shared/components/MapBoxViewer/mapboxAddress'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/client/src/shared/components/shadcn-ui/command'
import {
  Field,
  FieldLabel,
} from '~/client/src/shared/components/shadcn-ui/field'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { IMapBoxFeature } from '~/client/src/shared/interfaces/IMapBoxFeature'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  id: string
  label: string
  fullAddressLabel: string
  items: IMapBoxFeature[]
  onItemSelect: (item: IMapBoxFeature) => void
  onQueryChange: (query: string) => Promise<void>
}

const ProjectMapboxAddressField: React.FC<IProps> = ({
  id,
  label,
  fullAddressLabel,
  items,
  onItemSelect,
  onQueryChange,
}: IProps) => {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)

    if (nextOpen) {
      setQuery('')
    }
  }

  const handleQueryChange = (nextQuery: string) => {
    setQuery(nextQuery)
    onQueryChange(nextQuery)
  }

  const handleSelect = (item: IMapBoxFeature) => {
    onItemSelect(item)
    setOpen(false)
  }

  const isEmpty = Boolean(query.trim()) && items.length === 0

  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          id={id}
          render={
            <FieldTrigger
              title={fullAddressLabel}
              icon={<ChevronsUpDown className="tw:text-muted-foreground" />}
            />
          }
        >
          {fullAddressLabel}
        </PopoverTrigger>
        <PopoverContent align="start" className="tw:w-[350px] tw:p-0">
          <Command shouldFilter={false}>
            <CommandInput
              autoFocus
              value={query}
              placeholder={Localization.translator.search}
              onValueChange={handleQueryChange}
            />
            <CommandList>
              {isEmpty && (
                <div className="tw:py-6 tw:text-center tw:text-app-dense">
                  {Localization.translator.noResultsFound ||
                    Localization.translator.search}
                </div>
              )}
              <CommandGroup>
                {query.trim() &&
                  items.map(item => {
                    const addr = mapboxFeatureToAddress(item)
                    const addressParts = [addr.city, addr.state, addr.country]
                      .filter(Boolean)
                      .join(' ')

                    return (
                      <CommandItem
                        key={item.id}
                        value={item.place_name}
                        onSelect={() => handleSelect(item)}
                      >
                        <div className="tw:flex tw:min-w-0 tw:flex-col">
                          <span className="tw:truncate tw:text-app-dense-medium">
                            {addr.address}
                          </span>
                          <span className="tw:truncate tw:text-app-micro tw:text-muted-foreground">
                            {addressParts}
                          </span>
                        </div>
                      </CommandItem>
                    )
                  })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </Field>
  )
}

export default ProjectMapboxAddressField
